import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const DailyTracker = () => {
  const [income, setIncome] = useState(0);
  const [expenses, setExpenses] = useState(0);
  const [data, setData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  // Load stored data
  useEffect(() => {
    const storedData =
      JSON.parse(localStorage.getItem("dailyTrackerData")) || [];
    setData(storedData);

    const now = new Date();
    const lastReset = localStorage.getItem("lastReset");
    const lastResetDate = lastReset ? new Date(lastReset) : null;

    // Reset inputs at midnight
    if (!lastResetDate || now.toDateString() !== lastResetDate.toDateString()) {
      setIncome(0);
      setExpenses(0);
      localStorage.setItem("lastReset", now.toString());
    }
  }, []);

  // Update inputs when switching dates
  useEffect(() => {
    const existingEntry = data.find((entry) => entry.date === selectedDate);
    if (existingEntry) {
      setIncome(existingEntry.income);
      setExpenses(existingEntry.expenses);
    } else {
      setIncome(0);
      setExpenses(0);
    }
  }, [selectedDate, data]);

  // Save data locally whenever it changes
  useEffect(() => {
    localStorage.setItem("dailyTrackerData", JSON.stringify(data));
  }, [data]);

  const handleUpdate = () => {
    const updatedData = [...data];
    const dateIndex = updatedData.findIndex(
      (entry) => entry.date === selectedDate
    );

    if (dateIndex > -1) {
      updatedData[dateIndex] = {
        date: selectedDate,
        income,
        expenses,
      };
    } else {
      updatedData.push({
        date: selectedDate,
        income,
        expenses,
      });
    }

    setData(updatedData);
  };

  // Prepare chart data for income and expenses
  const chartData = {
    labels: data.map((entry) => entry.date),
    datasets: [
      {
        label: "Income",
        data: data.map((entry) => entry.income),
        borderColor: "#4CAF50",
        backgroundColor: "rgba(76, 175, 80, 0.2)",
        fill: true,
        tension: 0.4,
      },
      {
        label: "Expenses",
        data: data.map((entry) => -entry.expenses), // Negative to show downward movement
        borderColor: "#F44336",
        backgroundColor: "rgba(244, 67, 54, 0.2)",
        fill: true,
        tension: 0.4,
      },
    ],
  };

  return (
    <Container>
      <Left>
        <InputGroup>
          <label>Select Date:</label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
        </InputGroup>
        <InputGroup>
          <label>Income:</label>
          <input
            type="number"
            value={income}
            onChange={(e) => setIncome(Number(e.target.value))}
          />
        </InputGroup>
        <InputGroup>
          <label>Expenses:</label>
          <input
            type="number"
            value={expenses}
            onChange={(e) => setExpenses(Number(e.target.value))}
          />
        </InputGroup>
        <button onClick={handleUpdate}>Update</button>
      </Left>
      <Right>
        {data.length > 0 ? (
          <Line
            data={chartData}
            options={{
              responsive: true,
              plugins: {
                legend: { display: true, position: "top" },
              },
              scales: {
                x: { title: { display: true, text: "Date" } },
                y: {
                  title: { display: true, text: "Value ($)" },
                  beginAtZero: true,
                },
              },
            }}
          />
        ) : (
          <p>No data available yet!</p>
        )}
      </Right>
      <h2>Daily Tracker</h2>
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  display: flex;
  width: 48%;
  flex-direction: column-reverse;
  align-items: flex-start;
  gap: 20px;
  padding: 20px;
  background-color: #171c29;
  color: white;
  border-radius: 20px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  h2 {
    font-weight: 300;
    text-align: center;
    margin-bottom: 10px;
    margin: auto;
  }
  @media (max-width: 1100px) {
    width: 100%;
  }
`;

const Left = styled.div`
  padding: 20px;
  background-color: #1f2436;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  flex-direction: row;
  gap: 20px;
  width: 100%;

  button {
    margin-top: 10px;
    padding: 10px;
    background-color: #4caf50;
    color: white;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    transition: background-color 0.3s ease;

    &:hover {
      background-color: #45a049;
    }
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;

  label {
    font-size: 14px;
    font-weight: 500;
  }

  input {
    padding: 10px;
    border: none;
    border-radius: 10px;
    outline: none;
    background-color: #252a3e;
    color: white;
  }
`;

const Right = styled.div`
  width: 100%;
`;

export default DailyTracker;
